import { useInfiniteQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrFilter } from '@nostrify/nostrify';
import { Deck, DECK_KIND, isDeckEvent, transformDeckEvent } from '@/lib/nostr/deck';
import { useCurrentUser } from './useCurrentUser';

interface BrowseDecksFilters {
  commanders?: string[];
  companion?: string;
  cards?: string[];
  format?: string;
}

interface BrowseDecksResponse {
  decks: Deck[];
  nextCursor?: string;
  hasMore: boolean;
}

export const useBrowseDecks = (filters: BrowseDecksFilters = {}) => {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useInfiniteQuery<BrowseDecksResponse>({
    queryKey: ['browse-decks', filters],
    queryFn: async ({ pageParam }) => {
      const limit = 50;
      const until = pageParam ? parseInt(pageParam as string, 10) : undefined;

      // Build filter array
      const filter: NostrFilter = {
        kinds: [DECK_KIND],
        limit,
      };

      if (until) {
        filter.until = until;
      }

      // Add commanders filter (supports multiple commanders)
      if (filters.commanders && filters.commanders.length > 0) {
        filter['#C'] = filters.commanders;
      }

      // Add companion filter
      if (filters.companion) {
        filter['#S'] = [filters.companion];
      }

      // Add format filter
      if (filters.format && filters.format !== 'all') {
        filter['#t'] = [filters.format];
      }

      // Add card filters - we need to handle multiple cards
      if (filters.cards && filters.cards.length > 0) {
        // For multiple cards, we need to use OR logic
        // We'll fetch decks that contain ANY of the specified cards
        // This is more efficient than trying to find decks with ALL cards
        filter['#c'] = filters.cards;
      }

      const events = await nostr.query([filter]);

      const validDecks = events
        .filter(isDeckEvent)
        .map(transformDeckEvent)
        .filter(deck => {
          // Filter out unlisted decks unless they belong to the current user
          const isUnlisted = deck.event.tags.some(t => t[0] === 't' && t[1] === 'unlisted');
          if (isUnlisted && deck.event.pubkey !== user?.pubkey) {
            return false;
          }
          return true;
        });

      // For multiple card filters, we need to do additional filtering client-side
      // to ensure decks contain ALL specified cards (not just ANY)
      let filteredDecks = validDecks;
      if (filters.cards && filters.cards.length > 0) {
        filteredDecks = validDecks.filter(deck => {
          const deckCardNames = new Set(deck.cards.map(c => c.name.toLowerCase()));
          return filters.cards!.every(cardName =>
            deckCardNames.has(cardName.toLowerCase())
          );
        });
      }

      // For commanders filter, we need to do case-insensitive matching
      if (filters.commanders && filters.commanders.length > 0) {
        const commandersLower = filters.commanders.map(c => c.toLowerCase());
        filteredDecks = filteredDecks.filter(deck =>
          commandersLower.every(cmdFilter =>
            deck.commanders.some(deckCmd => deckCmd.toLowerCase().includes(cmdFilter))
          )
        );
      }

      if (filters.companion) {
        const companionLower = filters.companion.toLowerCase();
        filteredDecks = filteredDecks.filter(deck =>
          deck.companion?.toLowerCase().includes(companionLower)
        );
      }

      // Get the last event's created_at for pagination
      const lastEvent = events[events.length - 1];
      const nextCursor = lastEvent ? lastEvent.created_at.toString() : undefined;

      return {
        decks: filteredDecks,
        nextCursor,
        hasMore: filteredDecks.length === limit,
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};